import type {
  CabinetEventKind,
  CabinetEventView,
  CabinetEventsResult,
  MarkEventsReadRequest,
} from "@planerius/contracts/cabinet-events"

import { apiRequest } from "./http"
import { newIdempotencyKey } from "./idempotency"

/**
 * Центр уведомлений (1-Э1.9): лента, отметки о прочтении и «разобрано».
 *
 * КУРСОР, А НЕ НОМЕР СТРАНИЦЫ. Лента пополняется сверху: пока человек читает первую
 * страницу, приходят новые события, и «страница 2» по номеру показала бы сдвинутые
 * строки — то же событие дважды или пропущенное. Поэтому «Показать ещё» передаёт курсор
 * последней прочитанной строки, а не её номер.
 */

export interface EventsFeedParams {
  readonly onlyAction: boolean
  readonly kinds?: readonly CabinetEventKind[] | undefined
  readonly branchId?: string | undefined
  readonly cursor?: string | undefined
  readonly limit?: number | undefined
}

export async function fetchEvents(
  params: EventsFeedParams,
  signal?: AbortSignal,
): Promise<CabinetEventsResult> {
  return apiRequest<CabinetEventsResult>("/cabinet/events", {
    query: {
      ...(params.onlyAction ? { onlyAction: true } : {}),
      ...(params.kinds === undefined || params.kinds.length === 0 ? {} : { kinds: [...params.kinds] }),
      ...(params.branchId === undefined ? {} : { branchId: params.branchId }),
      ...(params.cursor === undefined ? {} : { cursor: params.cursor }),
      ...(params.limit === undefined ? {} : { limit: params.limit }),
    },
    signal,
  })
}

/**
 * «Прочитано». Пустой список — все информационные строки: это кнопка «Прочитать все».
 *
 * Дела этой кнопкой НЕ гасятся (так решает сервер): иначе она стала бы способом обнулить
 * счётчик, ничего не сделав, — и счётчик перестал бы значить «столько дел».
 *
 * Ключ идемпотентности новый на каждое НАЖАТИЕ: повторная отметка прочтения ничего
 * не портит (строка уже прочитана), а сохранённый ответ на «прочитать все» вернул бы
 * прежнее число и соврал бы о том, что сейчас произошло.
 */
export async function markEventsRead(
  eventIds: readonly string[],
  /** Срез «прочитать все» — филиал и виды ленты на экране. */
  slice: { readonly branchId?: string; readonly kinds?: readonly CabinetEventKind[] } = {},
  signal?: AbortSignal,
): Promise<{ readonly marked: number }> {
  const body: MarkEventsReadRequest = {
    eventIds: [...eventIds],
    ...(slice.branchId === undefined ? {} : { branchId: slice.branchId }),
    ...(slice.kinds === undefined ? {} : { kinds: [...slice.kinds] }),
  }
  return apiRequest<{ readonly marked: number }>("/cabinet/events/read", {
    method: "POST",
    body,
    idempotencyKey: newIdempotencyKey("events-read"),
    signal,
  })
}

/** «Разобрано»: закрыть дело. Чужое событие сервер не покажет и не закроет (404). */
export async function closeEvent(eventId: string, signal?: AbortSignal): Promise<CabinetEventView> {
  return apiRequest<CabinetEventView>(`/cabinet/events/${encodeURIComponent(eventId)}/close`, {
    method: "POST",
    idempotencyKey: newIdempotencyKey(`events-close:${eventId}`),
    signal,
  })
}
